/**
 * Animated Progress Bar Component
 * Smooth progress bar animations with various styles and configurations
 */

import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { animated, useSpring } from '@react-spring/web';
import { 
  useScrollAnimation, 
  useAnimationConfig, 
  useReducedMotion, 
  usePerformanceMonitor 
} from '../lib/animations';

// Progress bar configuration
export interface ProgressBarConfig {
  value: number; // 0-100
  maxValue?: number;
  height?: number;
  borderRadius?: number;
  backgroundColor?: string;
  fillColor?: string;
  gradientColors?: string[];
  showLabel?: boolean;
  labelPosition?: 'inside' | 'outside' | 'top' | 'bottom';
  labelFormat?: (value: number, maxValue: number) => string;
  animationDuration?: number;
  animationDelay?: number;
  animationEasing?: string;
  showPercentage?: boolean;
  showValue?: boolean;
  striped?: boolean;
  animated?: boolean; // For striped animation
  glowEffect?: boolean;
  pulseOnComplete?: boolean;
}

interface AnimatedProgressBarProps {
  config: ProgressBarConfig;
  className?: string;
  triggerOnScroll?: boolean;
  threshold?: number;
  onAnimationStart?: () => void;
  onAnimationComplete?: () => void;
  onValueChange?: (value: number) => void;
}

// Default configuration
const defaultConfig: Partial<ProgressBarConfig> = {
  maxValue: 100,
  height: 12,
  borderRadius: 6,
  backgroundColor: '#e5e7eb',
  fillColor: '#3b82f6',
  showLabel: true,
  labelPosition: 'outside',
  animationDuration: 2,
  animationDelay: 0,
  animationEasing: 'easeOutCubic',
  showPercentage: true,
  showValue: false,
  striped: false,
  animated: false,
  glowEffect: false,
  pulseOnComplete: false
};

export const AnimatedProgressBar: React.FC<AnimatedProgressBarProps> = ({
  config: userConfig,
  className = '',
  triggerOnScroll = true,
  threshold = 0.1,
  onAnimationStart,
  onAnimationComplete,
  onValueChange
}) => {
  const config = { ...defaultConfig, ...userConfig };
  const [isVisible, setIsVisible] = useState(!triggerOnScroll);
  const [animationComplete, setAnimationComplete] = useState(false);
  const animationConfig = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const scrollAnimation = useScrollAnimation(threshold, true);
  const { trackAnimation } = usePerformanceMonitor();

  // Calculate percentage
  const percentage = Math.min((config.value / (config.maxValue || 100)) * 100, 100);

  // Spring animation for progress bar
  const progressSpring = useSpring({
    width: isVisible ? `${percentage}%` : '0%',
    config: {
      tension: 120,
      friction: 40,
      duration: isReducedMotion ? 0 : (config.animationDuration || 2) * 1000
    },
    delay: (config.animationDelay || 0) * 1000,
    onStart: () => {
      trackAnimation.start();
      if (onAnimationStart) onAnimationStart();
    },
    onRest: () => {
      setAnimationComplete(true);
      trackAnimation.end();
      if (onAnimationComplete) onAnimationComplete();
    },
    onChange: (result) => {
      if (onValueChange) {
        const currentPercentage = parseFloat(result.value.width.replace('%', ''));
        const currentValue = (currentPercentage / 100) * (config.maxValue || 100);
        onValueChange(currentValue);
      }
    },
    immediate: isReducedMotion
  });

  // Glow effect animation
  const glowSpring = useSpring({
    opacity: config.glowEffect && isVisible ? 0.6 : 0,
    scale: config.glowEffect && isVisible ? 1.1 : 1,
    config: {
      tension: 200,
      friction: 50
    },
    immediate: isReducedMotion
  });

  // Pulse effect for completion
  const pulseSpring = useSpring({
    scale: config.pulseOnComplete && animationComplete ? [1, 1.05, 1] : 1,
    config: {
      tension: 300,
      friction: 30
    },
    immediate: isReducedMotion || !config.pulseOnComplete
  });

  // Handle scroll trigger
  useEffect(() => {
    if (triggerOnScroll) {
      setIsVisible(scrollAnimation.isVisible);
    }
  }, [triggerOnScroll, scrollAnimation.isVisible]);

  // Default label formatter
  const defaultLabelFormat = (value: number, maxValue: number) => {
    const parts = [];
    if (config.showValue) parts.push(`${Math.round(value)}`);
    if (config.showPercentage) parts.push(`${Math.round((value / maxValue) * 100)}%`);
    return parts.join(' - ');
  };

  const labelFormat = config.labelFormat || defaultLabelFormat;
  const labelText = labelFormat(config.value, config.maxValue || 100);

  // Create gradient background if gradient colors are provided
  const fillStyle = config.gradientColors && config.gradientColors.length > 1
    ? {
        background: `linear-gradient(90deg, ${config.gradientColors.join(', ')})`
      }
    : {
        backgroundColor: config.fillColor
      };

  // Striped pattern styles
  const stripedStyle = config.striped ? {
    backgroundImage: `linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.15) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.15) 50%,
      rgba(255, 255, 255, 0.15) 75%,
      transparent 75%,
      transparent
    )`,
    backgroundSize: '1rem 1rem'
  } : {};

  // Animated stripes
  const animatedStripesStyle = config.animated && config.striped ? {
    animation: 'progress-bar-stripes 1s linear infinite'
  } : {};

  return (
    <div 
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={`relative w-full ${className}`}
    >
      {/* Top Label */}
      {config.showLabel && config.labelPosition === 'top' && (
        <motion.div
          className="flex justify-between items-center mb-2 text-sm font-medium text-gray-700"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : -10 }}
          transition={{
            duration: animationConfig.customTimings.fast,
            delay: (config.animationDelay || 0)
          }}
        >
          <span>{labelText}</span>
        </motion.div>
      )}

      {/* Progress Bar Container */}
      <animated.div
        style={{
          ...pulseSpring,
          height: config.height,
          borderRadius: config.borderRadius,
          backgroundColor: config.backgroundColor
        }}
        className="relative overflow-hidden"
      >
        {/* Glow Effect */}
        {config.glowEffect && (
          <animated.div
            style={{
              ...glowSpring,
              position: 'absolute',
              inset: -2,
              borderRadius: (config.borderRadius || 0) + 2,
              background: `linear-gradient(90deg, transparent, ${config.fillColor}40, transparent)`,
              filter: 'blur(4px)',
              zIndex: -1
            }}
          />
        )}

        {/* Progress Fill */}
        <animated.div
          style={{
            ...progressSpring,
            height: '100%',
            borderRadius: config.borderRadius,
            ...fillStyle,
            ...stripedStyle,
            ...animatedStripesStyle
          }}
          className="transition-all duration-300"
        />

        {/* Inside Label */}
        {config.showLabel && config.labelPosition === 'inside' && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible && percentage > 20 ? 1 : 0 }}
            transition={{
              duration: animationConfig.customTimings.fast,
              delay: (config.animationDelay || 0) + 0.5
            }}
          >
            {labelText}
          </motion.div>
        )}

        {/* Shimmer Effect */}
        <motion.div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            transform: 'translateX(-100%)'
          }}
          animate={{
            transform: isVisible ? ['translateX(-100%)', 'translateX(100%)'] : 'translateX(-100%)'
          }}
          transition={{
            duration: 1.5,
            delay: (config.animationDelay || 0) + 0.5,
            ease: 'easeInOut'
          }}
        />
      </animated.div>

      {/* Bottom/Outside Label */}
      {config.showLabel && (config.labelPosition === 'bottom' || config.labelPosition === 'outside') && (
        <motion.div
          className={`flex justify-between items-center text-sm font-medium text-gray-700 ${
            config.labelPosition === 'bottom' ? 'mt-2' : 'mt-1'
          }`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 10 }}
          transition={{
            duration: animationConfig.customTimings.fast,
            delay: (config.animationDelay || 0) + 0.3
          }}
        >
          <span>{labelText}</span>
          {config.maxValue && config.maxValue !== 100 && (
            <span className="text-gray-500">/ {config.maxValue}</span>
          )}
        </motion.div>
      )}

      {/* CSS for animated stripes */}
      <style jsx>{`
        @keyframes progress-bar-stripes {
          0% {
            background-position: 1rem 0;
          }
          100% {
            background-position: 0 0;
          }
        }
      `}</style>
    </div>
  );
};

// Multi-progress bar component for comparing multiple values
interface MultiProgressBarProps {
  bars: Array<ProgressBarConfig & { id: string; label?: string }>;
  className?: string;
  triggerOnScroll?: boolean;
  threshold?: number;
  staggerDelay?: number;
  showComparison?: boolean;
}

export const MultiProgressBar: React.FC<MultiProgressBarProps> = ({
  bars,
  className = '',
  triggerOnScroll = true,
  threshold = 0.1,
  staggerDelay = 0.2,
  showComparison = false
}) => {
  const scrollAnimation = useScrollAnimation(threshold, true);
  const isVisible = triggerOnScroll ? scrollAnimation.isVisible : true;

  // Find the maximum value for comparison
  const maxValue = Math.max(...bars.map(bar => bar.value));

  return (
    <div 
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={`space-y-4 ${className}`}
    >
      {bars.map((bar, index) => (
        <div key={bar.id} className="space-y-2">
          {bar.label && (
            <motion.h4
              className="text-sm font-medium text-gray-900"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
              transition={{
                duration: 0.3,
                delay: index * staggerDelay
              }}
            >
              {bar.label}
            </motion.h4>
          )}
          
          <AnimatedProgressBar
            config={{
              ...bar,
              animationDelay: (bar.animationDelay || 0) + (index * staggerDelay),
              maxValue: showComparison ? maxValue : bar.maxValue
            }}
            triggerOnScroll={false} // We handle visibility at the parent level
            className="w-full"
          />
        </div>
      ))}
    </div>
  );
};

export default AnimatedProgressBar;